import { ApplicationModuleFieldLookup } from "../api/applicationModuleFields";
import {
    backendFormatToDaySchedule,
    convert12HourTo24Hour,
    isValidDayFormat
} from "../../../shared/utils/weeklyScheduleFormatters";
import {
    disabledDaysToSpecialDates,
    formatDateForBackend,
    SpecialDate,
    specialDatesToDisabledDays
} from "../../../shared/utils/scheduleFormatters";

export interface WeeklyHoursAnswerValue {
    mondayHours: string;
    tuesdayHours: string;
    wednesdayHours: string;
    thursdayHours: string;
    fridayHours: string;
    saturdayHours: string;
    sundayHours: string;
}

export interface HolidayOption {
    key: string;
    label: string;
    date: string;
    year: number;
}

export interface ProcedureTypeAnswerItem {
    additionalProcedureTypeIdsToLookUp: null;
    appointmentDuration?: number;
    procedureTypeDescription: string;
    procedureTypeId?: string;
    isCleaningAppointment?: boolean;
    isEmergencyType?: boolean;
}

export type SpecialDatesAnswerValue = SpecialDate[];

export type MultiChoiceAnswerValue = string[];

export type ProcedureTypeAnswerValue = ProcedureTypeAnswerItem[];

export interface InsuranceCoverageAnswerValue {
    accepted: string;
    outOfNetwork: string;
    notAccepted: string;
}

export type OnboardingAnswerValue = string | WeeklyHoursAnswerValue | SpecialDatesAnswerValue | MultiChoiceAnswerValue | ProcedureTypeAnswerValue | InsuranceCoverageAnswerValue;

export const dayVariableMap: Array<{ key: keyof WeeklyHoursAnswerValue; variable: string; label: string }> = [
    { key: "mondayHours", variable: "{MONDAY}", label: "Monday" },
    { key: "tuesdayHours", variable: "{TUESDAY}", label: "Tuesday" },
    { key: "wednesdayHours", variable: "{WEDNESDAY}", label: "Wednesday" },
    { key: "thursdayHours", variable: "{THURSDAY}", label: "Thursday" },
    { key: "fridayHours", variable: "{FRIDAY}", label: "Friday" },
    { key: "saturdayHours", variable: "{SATURDAY}", label: "Saturday" },
    { key: "sundayHours", variable: "{SUNDAY}", label: "Sunday" }
];

const fallbackHours: WeeklyHoursAnswerValue = {
    mondayHours: "9:00 AM - 9:00 PM",
    tuesdayHours: "9:00 AM - 9:00 PM",
    wednesdayHours: "9:00 AM - 9:00 PM",
    thursdayHours: "9:00 AM - 9:00 PM",
    fridayHours: "9:00 AM - 10:00 PM",
    saturdayHours: "10:00 AM - 10:00 PM",
    sundayHours: "10:00 AM - 8:00 PM"
};

export const isWeeklyHoursAnswerValue = (value: OnboardingAnswerValue): value is WeeklyHoursAnswerValue => {
    return typeof value === "object" && value !== null && "mondayHours" in value;
};

export const isInsuranceCoverageAnswerValue = (value: OnboardingAnswerValue | unknown): value is InsuranceCoverageAnswerValue => {
    return typeof value === "object" && value !== null && "accepted" in value && "outOfNetwork" in value && "notAccepted" in value;
};

export const isSpecialDatesAnswerValue = (value: OnboardingAnswerValue | unknown): value is SpecialDatesAnswerValue => {
    return Array.isArray(value) && value.every(entry => typeof entry === "object" && entry !== null && "date" in entry && "isClosedAllDay" in entry);
};

export const isMultiChoiceAnswerValue = (value: OnboardingAnswerValue | unknown): value is MultiChoiceAnswerValue => {
    return Array.isArray(value) && value.every(entry => typeof entry === "string");
};

export const isProcedureTypeAnswerValue = (value: OnboardingAnswerValue | unknown): value is ProcedureTypeAnswerValue => {
    return Array.isArray(value) && value.every(entry => typeof entry === "object" && entry !== null && "procedureTypeDescription" in entry && "additionalProcedureTypeIdsToLookUp" in entry);
};

export const normalizeProcedureTypeAnswerValue = (value: unknown): ProcedureTypeAnswerValue => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map(entry => ({
        additionalProcedureTypeIdsToLookUp: null,
        appointmentDuration: typeof entry?.appointmentDuration === "number" && !Number.isNaN(entry.appointmentDuration)
            ? entry.appointmentDuration
            : (typeof entry?.appointmentDuration === "string" && entry.appointmentDuration.trim() !== "" && !Number.isNaN(parseInt(entry.appointmentDuration, 10))
                ? parseInt(entry.appointmentDuration, 10)
                : undefined),
        procedureTypeDescription: typeof entry?.procedureTypeDescription === "string" ? entry.procedureTypeDescription : "",
        procedureTypeId: typeof entry?.procedureTypeId === "string" ? entry.procedureTypeId : "",
        isCleaningAppointment: !!entry?.isCleaningAppointment,
        isEmergencyType: !!entry?.isEmergencyType
    }));
};

export const formatProcedureTypeAnswerValue = (value: ProcedureTypeAnswerValue): string => {
    return JSON.stringify(normalizeProcedureTypeAnswerValue(value).map(entry => ({
        additionalProcedureTypeIdsToLookUp: null,
        appointmentDuration: entry.appointmentDuration,
        procedureTypeDescription: entry.procedureTypeDescription,
        procedureTypeId: entry.procedureTypeId || "",
        isCleaningAppointment: entry.isCleaningAppointment,
        isEmergencyType: entry.isEmergencyType
    })), null, 2);
};

export const createDefaultWeeklyHoursAnswer = (override?: Partial<WeeklyHoursAnswerValue>): WeeklyHoursAnswerValue => ({
    ...fallbackHours,
    ...override
});

export const getModuleFieldValue = (
    lookup: ApplicationModuleFieldLookup,
    associatedVariable: string,
    fallback = ""
): string => {
    const field = lookup?.[associatedVariable];
    if (field?.currentValue !== undefined && field.currentValue !== null && field.currentValue !== "") {
        if (field.currentValue === "-1") {
            return "";
        }

        if (field.currentValue.trim() !== associatedVariable.trim()) {
            return field.currentValue;
        }
    }

    if (field?.defaultValue !== undefined && field.defaultValue !== null && field.defaultValue !== "") {
        if (field.defaultValue === "-1") {
            return "";
        }

        if (field.defaultValue.trim() !== associatedVariable.trim()) {
            return field.defaultValue;
        }
    }

    return fallback === "-1" ? "" : fallback;
};

export const getFirstModuleFieldValue = (
    lookup: ApplicationModuleFieldLookup,
    associatedVariables: string[],
    fallback = ""
): string => {
    for (const associatedVariable of associatedVariables) {
        const value = getModuleFieldValue(lookup, associatedVariable, "");
        if (value !== "") {
            return value;
        }
    }

    return fallback;
};

export const parseBooleanString = (value: string, fallback = ""): string => {
    const normalized = (value || "").trim().toLowerCase();

    if (["true", "yes", "y", "1"].includes(normalized)) {
        return "yes";
    }

    if (["false", "no", "n", "0"].includes(normalized)) {
        return "no";
    }

    return fallback;
};

export const booleanAnswerToString = (value: string): string => value === "yes" ? "true" : "false";

export const inverseBooleanAnswerToString = (value: string): string => value === "yes" ? "false" : "true";

export const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, "").slice(0, 11);

    if (digits.length === 0) {
        return "";
    }

    const hasCountryCode = digits.length > 10 && digits.startsWith("1");
    const workingDigits = hasCountryCode ? digits.slice(1) : digits.slice(0, 10);
    const area = workingDigits.slice(0, 3);
    const prefix = workingDigits.slice(3, 6);
    const line = workingDigits.slice(6, 10);

    let formatted = "";

    if (area.length > 0) {
        formatted = `(${area}`;
    }

    if (area.length === 3) {
        formatted += ")";
    }

    if (prefix.length > 0) {
        formatted += ` ${prefix}`;
    }

    if (line.length > 0) {
        formatted += `-${line}`;
    }

    if (hasCountryCode) {
        return `1 ${formatted}`.trim();
    }

    return formatted.trim();
};

export const getPhoneDigitsForSave = (value: string): string => {
    const digits = value.replace(/\D/g, "");

    if (!digits) {
        return "";
    }

    if (digits.length === 10) {
        return `1${digits}`;
    }

    return digits.slice(0, 11);
};

const normalizeTimeInput = (value: string): string => {
    const trimmed = value.trim().toUpperCase().replace(/\s+/g, " ");
    const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*([AP]M)$/);

    if (!match) {
        return trimmed;
    }

    const hour = match[1];
    const minute = match[2] || "00";
    const meridiem = match[3];

    return `${parseInt(hour, 10)}:${minute} ${meridiem}`;
};

const parseDisplayHoursPeriod = (period: string): string | null => {
    const normalized = period.replace(/\s+to\s+/i, " - ").trim();
    const segments = normalized.split(/\s*-\s*/);

    if (segments.length !== 2) {
        return null;
    }

    try {
        return `${convert12HourTo24Hour(normalizeTimeInput(segments[0]))},${convert12HourTo24Hour(normalizeTimeInput(segments[1]))}`;
    } catch (_) {
        return null;
    }
};

export const formatHoursForDisplay = (backendValue: string): string => {
    const trimmed = (backendValue || "").trim();

    if (!trimmed) {
        return "";
    }

    if (trimmed === "00:00,00:01") {
        return "Closed";
    }

    if (trimmed === "00:00,23:59") {
        return "Open 24 hours";
    }

    if (!isValidDayFormat(trimmed)) {
        return trimmed;
    }

    const schedule = backendFormatToDaySchedule("Day", trimmed);
    return schedule.timeRanges.map(range => `${range.startTime} - ${range.endTime}`).join("; ");
};

export const parseHoursForSave = (displayValue: string): string => {
    const trimmed = (displayValue || "").trim();

    if (!trimmed) {
        return "";
    }

    if (isValidDayFormat(trimmed)) {
        return trimmed;
    }

    const normalized = trimmed.toLowerCase();
    if (["closed", "closed all day"].includes(normalized)) {
        return "00:00,00:01";
    }

    if (["open 24 hours", "open all day", "24 hours", "24/7"].includes(normalized)) {
        return "00:00,23:59";
    }

    const periods = trimmed.split(/\s*[;|]\s*/).map(parseDisplayHoursPeriod).filter(Boolean) as string[];
    return periods.length > 0 ? periods.join("|") : trimmed;
};

export const getWeeklyHoursAnswerFromLookup = (lookup: ApplicationModuleFieldLookup): WeeklyHoursAnswerValue => {
    return dayVariableMap.reduce((accumulator, entry) => {
        accumulator[entry.key] = formatHoursForDisplay(getModuleFieldValue(lookup, entry.variable, ""));
        return accumulator;
    }, {} as WeeklyHoursAnswerValue);
};

export const getWeeklyHoursUpdates = (answer: WeeklyHoursAnswerValue) => {
    return dayVariableMap.map(entry => ({
        associatedVariable: entry.variable,
        value: parseHoursForSave(answer[entry.key])
    }));
};

const nthWeekdayOfMonth = (year: number, monthIndex: number, weekday: number, occurrence: number): string => {
    const date = new Date(Date.UTC(year, monthIndex, 1));
    const offset = (weekday - date.getUTCDay() + 7) % 7;
    date.setUTCDate(1 + offset + ((occurrence - 1) * 7));
    return date.toISOString().slice(0, 10);
};

const lastWeekdayOfMonth = (year: number, monthIndex: number, weekday: number): string => {
    const date = new Date(Date.UTC(year, monthIndex + 1, 0));
    const offset = (date.getUTCDay() - weekday + 7) % 7;
    date.setUTCDate(date.getUTCDate() - offset);
    return date.toISOString().slice(0, 10);
};

const buildHolidayOption = (label: string, date: string): HolidayOption => ({
    key: `${label}-${date}`,
    label,
    date,
    year: parseInt(date.slice(0, 4), 10)
});

const formatSpecialDateLine = (specialDate: SpecialDate): string => {
    const backendDate = formatDateForBackend(specialDate.date);
    if (!backendDate) {
        return "";
    }

    if (specialDate.isClosedAllDay || specialDate.timeRanges.length === 0) {
        return backendDate;
    }

    const ranges = specialDate.timeRanges
        .map(timeRange => `${timeRange.startTime} - ${timeRange.endTime}`)
        .join(", ");

    return `${backendDate} (${ranges})`;
};

export const normalizeSpecialDatesAnswer = (value: Partial<SpecialDate>[] | undefined | null): SpecialDatesAnswerValue => {
    if (!Array.isArray(value)) {
        return [];
    }

    const seenDates = new Set<string>();

    return value.reduce((accumulator, entry, index) => {
        const date = typeof entry?.date === "string" ? entry.date.trim() : "";
        if (!date || seenDates.has(date)) {
            return accumulator;
        }

        seenDates.add(date);
        accumulator.push({
            id: typeof entry?.id === "string" && entry.id.trim() ? entry.id : `special-date-${date}-${index}`,
            date,
            isClosedAllDay: entry?.isClosedAllDay !== false,
            timeRanges: Array.isArray(entry?.timeRanges) ? entry.timeRanges.map((timeRange, timeIndex) => ({
                id: typeof timeRange?.id === "string" && timeRange.id.trim() ? timeRange.id : `special-time-${date}-${timeIndex}`,
                startTime: typeof timeRange?.startTime === "string" ? timeRange.startTime : "",
                endTime: typeof timeRange?.endTime === "string" ? timeRange.endTime : ""
            })).filter(timeRange => timeRange.startTime && timeRange.endTime) : []
        });

        return accumulator;
    }, [] as SpecialDatesAnswerValue).sort((left, right) => left.date.localeCompare(right.date));
};

export const getSpecialDatesAnswerFromLookup = (lookup: ApplicationModuleFieldLookup): SpecialDatesAnswerValue => {
    const currentYear = new Date().getFullYear();
    const holidayDateSet = new Set(
        generateCommonUsHolidayOptions(currentYear - 1, currentYear + 5).map(h => h.date)
    );
    return normalizeSpecialDatesAnswer(disabledDaysToSpecialDates(getModuleFieldValue(lookup, "DISABLED_DAYS"), holidayDateSet));
};

export const createHolidaySpecialDate = (date: string): SpecialDate => ({
    id: `holiday-${date}`,
    date,
    isClosedAllDay: true,
    timeRanges: []
});

export const createCustomSpecialDate = (date: string, timeRanges?: SpecialDate["timeRanges"]): SpecialDate => ({
    id: `custom-${date}`,
    date,
    isClosedAllDay: !timeRanges || timeRanges.length === 0,
    timeRanges: timeRanges ?? []
});

export const parseSpecialDatesForSave = (value: SpecialDatesAnswerValue | string): string => {
    if (typeof value === "string") {
        if (!value.trim()) {
            return "";
        }

        return value
            .split(/\n|,/) 
            .map(entry => entry.trim())
            .filter(Boolean)
            .join(",");
    }

    return specialDatesToDisabledDays(normalizeSpecialDatesAnswer(value));
};

export const formatSpecialDatesForDisplay = (value: SpecialDatesAnswerValue | string): string => {
    if (typeof value === "string") {
        if (!value.trim()) {
            return "";
        }

        return value
            .split(",")
            .map(entry => entry.trim())
            .filter(Boolean)
            .join("\n");
    }

    return normalizeSpecialDatesAnswer(value)
        .map(formatSpecialDateLine)
        .filter(Boolean)
        .join("\n");
};

export const generateCommonUsHolidayOptions = (startYear: number, endYear: number): HolidayOption[] => {
    const holidays: HolidayOption[] = [];

    for (let year = startYear; year <= endYear; year += 1) {
        holidays.push(buildHolidayOption("New Year's Day", `${year}-01-01`));
        holidays.push(buildHolidayOption("Martin Luther King Jr. Day", nthWeekdayOfMonth(year, 0, 1, 3)));
        holidays.push(buildHolidayOption("Presidents Day", nthWeekdayOfMonth(year, 1, 1, 3)));
        holidays.push(buildHolidayOption("Memorial Day", lastWeekdayOfMonth(year, 4, 1)));
        holidays.push(buildHolidayOption("Juneteenth", `${year}-06-19`));
        holidays.push(buildHolidayOption("Independence Day", `${year}-07-04`));
        holidays.push(buildHolidayOption("Labor Day", nthWeekdayOfMonth(year, 8, 1, 1)));
        holidays.push(buildHolidayOption("Columbus Day", nthWeekdayOfMonth(year, 9, 1, 2)));
        holidays.push(buildHolidayOption("Veterans Day", `${year}-11-11`));
        holidays.push(buildHolidayOption("Thanksgiving Day", nthWeekdayOfMonth(year, 10, 4, 4)));
        holidays.push(buildHolidayOption("Christmas Eve", `${year}-12-24`));
        holidays.push(buildHolidayOption("Christmas Day", `${year}-12-25`));
    }

    return holidays;
};

export const joinLabeledSections = (sections: Array<{ title: string; value: string }>): string => {
    return sections
        .map(section => ({
            title: section.title,
            value: section.value?.trim() || ""
        }))
        .filter(section => section.value.length > 0)
        .map(section => `${section.title}: ${section.value}`)
        .join("\n\n");
};

export const sanitizeMultilineText = (value: string): string => value?.trim() || "";

export const buildClosedGreeting = (value: string): string => {
    const trimmed = sanitizeMultilineText(value);

    if (!trimmed) {
        return "";
    }

    const suffix = "We're currently closed, but I may still be able to help answer your question.";
    return trimmed.endsWith(suffix) ? trimmed : `${trimmed} ${suffix}`;
};

export const parseInsuranceCoverageFromPrompt = (promptValue: string): InsuranceCoverageAnswerValue => {
    const acceptedHeader = "INSURANCE PROVIDERS ACCEPTED (IN-NETWORK):";
    const outOfNetworkHeader = "INSURANCE PROVIDERS ACCEPTED (OUT-OF-NETWORK):";
    const notAcceptedHeader = "INSURANCE PROVIDERS NOT ACCEPTED:";

    let accepted = "";
    let outOfNetwork = "";
    let notAccepted = "";

    const cleanPrompt = (promptValue || "").trim();

    if (cleanPrompt) {
        const acceptedIdx = cleanPrompt.indexOf(acceptedHeader);
        const outOfNetworkIdx = cleanPrompt.indexOf(outOfNetworkHeader);
        const notAcceptedIdx = cleanPrompt.indexOf(notAcceptedHeader);

        if (acceptedIdx !== -1 && outOfNetworkIdx !== -1 && notAcceptedIdx !== -1) {
            accepted = cleanPrompt.slice(acceptedIdx + acceptedHeader.length, outOfNetworkIdx).trim();
            outOfNetwork = cleanPrompt.slice(outOfNetworkIdx + outOfNetworkHeader.length, notAcceptedIdx).trim();
            notAccepted = cleanPrompt.slice(notAcceptedIdx + notAcceptedHeader.length).trim();
        } else {
            accepted = cleanPrompt;
        }
    }

    if (!accepted || accepted === "-") accepted = "-\n";
    if (!outOfNetwork || outOfNetwork === "-") outOfNetwork = "-\n";
    if (!notAccepted || notAccepted === "-") notAccepted = "-\n";

    return { accepted, outOfNetwork, notAccepted };
};

export const serializeInsuranceCoverageToPrompt = (value: InsuranceCoverageAnswerValue): string => {
    const accepted = (value.accepted || "-").trim();
    const outOfNetwork = (value.outOfNetwork || "-").trim();
    const notAccepted = (value.notAccepted || "-").trim();

    return `INSURANCE PROVIDERS ACCEPTED (IN-NETWORK):\n${accepted}\n\nINSURANCE PROVIDERS ACCEPTED (OUT-OF-NETWORK):\n${outOfNetwork}\n\nINSURANCE PROVIDERS NOT ACCEPTED:\n${notAccepted}`;
};

const numbersToWords: Record<number, string> = {
    1: "one", 2: "two", 3: "three", 4: "four", 5: "five",
    6: "six", 7: "seven", 8: "eight", 9: "nine", 10: "ten",
    11: "eleven", 12: "twelve", 13: "thirteen", 14: "fourteen", 15: "fifteen",
    16: "sixteen", 17: "seventeen", 18: "eighteen", 19: "nineteen", 20: "twenty",
    30: "thirty", 40: "forty", 50: "fifty"
};

const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function groupDaysToRanges(days: number[]): string {
    if (days.length === 0) return "";
    if (days.length === 7) return "every day";

    const ranges: number[][] = [];
    let currentRange: number[] = [days[0]];
    for (let i = 1; i < days.length; i++) {
        if (days[i] === days[i - 1] + 1) {
            currentRange.push(days[i]);
        } else {
            ranges.push(currentRange);
            currentRange = [days[i]];
        }
    }
    ranges.push(currentRange);

    const formattedRanges = ranges.map(range => {
        if (range.length === 1) {
            return dayNames[range[0]];
        } else if (range.length === 2) {
            return `${dayNames[range[0]]} and ${dayNames[range[1]]}`;
        } else {
            return `${dayNames[range[0]]} through ${dayNames[range[range.length - 1]]}`;
        }
    });

    if (formattedRanges.length === 1) {
        return formattedRanges[0];
    } else if (formattedRanges.length === 2) {
        return `${formattedRanges[0]} and ${formattedRanges[1]}`;
    } else {
        return `${formattedRanges.slice(0, -1).join(", ")}, and ${formattedRanges[formattedRanges.length - 1]}`;
    }
}

export const VOCAL_FORMATTING_PROMPT = `Format all responses for natural voice pronunciation. Spell out abbreviations and numbers that may be misread when spoken.

* **Times:** Spell out numbers followed by AM or PM: 9:00 AM -> nine AM; 5:30 PM -> five thirty PM.
* **Addresses:** Spell out abbreviations such as St -> Street, Rd -> Road, Ave -> Avenue, Blvd -> Boulevard, and Ste -> Suite.
* **States:** Always spell out state names: NE -> Nebraska, MA -> Massachusetts.

Optimize text for how it sounds when read aloud, not how it looks.`;

export const formatHoursParagraph = (storedHours: any, lookup: any): string => {
    const hoursByDay: string[] = [];
    const dayConfig = [
        { name: "Sunday", key: "sundayHours", variable: "{SUNDAY}" },
        { name: "Monday", key: "mondayHours", variable: "{MONDAY}" },
        { name: "Tuesday", key: "tuesdayHours", variable: "{TUESDAY}" },
        { name: "Wednesday", key: "wednesdayHours", variable: "{WEDNESDAY}" },
        { name: "Thursday", key: "thursdayHours", variable: "{THURSDAY}" },
        { name: "Friday", key: "fridayHours", variable: "{FRIDAY}" },
        { name: "Saturday", key: "saturdayHours", variable: "{SATURDAY}" }
    ];

    dayConfig.forEach((day, index) => {
        let displayHours = "";
        if (storedHours && typeof storedHours === "object" && !Array.isArray(storedHours)) {
            const hoursObj = storedHours as Record<string, string>;
            displayHours = (hoursObj[day.key] || "").trim();
        } else if (lookup) {
            const raw = getModuleFieldValue(lookup, day.variable, "");
            displayHours = formatHoursForDisplay(raw);
        }
        hoursByDay[index] = displayHours;
    });

    const hoursToDaysMap: Record<string, number[]> = {};
    hoursByDay.forEach((hours, dayIndex) => {
        const normHours = hours.trim();
        if (normHours && normHours.toLowerCase() !== "closed" && normHours !== "00:00,00:01") {
            if (!hoursToDaysMap[normHours]) {
                hoursToDaysMap[normHours] = [];
            }
            hoursToDaysMap[normHours].push(dayIndex);
        }
    });

    const groups = Object.entries(hoursToDaysMap);
    if (groups.length === 0) {
        return "";
    }

    const groupPhrases = groups.map(([hours, days]) => {
        const daysPhrase = groupDaysToRanges(days);
        const formattedRange = hours.replace(/\s*-\s*/g, " to ");
        return `${daysPhrase} from ${formattedRange}`;
    });

    let joinedPhrases = "";
    if (groupPhrases.length === 1) {
        joinedPhrases = groupPhrases[0];
    } else if (groupPhrases.length === 2) {
        joinedPhrases = `${groupPhrases[0]}, and ${groupPhrases[1]}`;
    } else {
        joinedPhrases = `${groupPhrases.slice(0, -1).join(", ")}, and ${groupPhrases[groupPhrases.length - 1]}`;
    }

    return `We are open ${joinedPhrases}.`;
};

export const formatPopularItemsParagraph = (popularItemsStr: string): string => {
    if (!popularItemsStr) return "";
    const items = popularItemsStr.split(",").map(s => s.trim()).filter(Boolean);
    if (items.length === 0) return "";

    const lowerItems = items.map((item, index) => {
        const lower = item.toLowerCase();
        if (index === 0) {
            if (lower.startsWith("the ") || lower.startsWith("a ") || lower.startsWith("an ")) {
                return lower;
            }
            return `the ${lower}`;
        }
        return lower;
    });

    let itemsList = "";
    if (lowerItems.length === 1) {
        itemsList = lowerItems[0];
    } else if (lowerItems.length === 2) {
        itemsList = `${lowerItems[0]} and ${lowerItems[1]}`;
    } else {
        itemsList = `${lowerItems.slice(0, -1).join(", ")}, and ${lowerItems[lowerItems.length - 1]}`;
    }

    return `Our most popular items are ${itemsList}.`;
};

export const extractUserCustomText = (savedValue: string): string => {
    if (!savedValue) return "";

    const promptKeyword = "Format all responses for natural voice pronunciation";
    const promptStartIdx = savedValue.indexOf(promptKeyword);
    let cleanValue = promptStartIdx !== -1 ? savedValue.slice(0, promptStartIdx) : savedValue;

    cleanValue = cleanValue
        .replace(/^Our address is.*$/mi, "")
        .replace(/^We are open.*$/mi, "")
        .replace(/^Closures:.*$/mi, "")
        .replace(/^Our most popular items are.*$/mi, "")
        .replace(/\n{2,}/g, "\n")
        .trim();

    return cleanValue;
};

export const formatLocationFaqParagraph = (userText: string, storedAnswers: any, moduleFieldLookup: any): string => {
    const parts: string[] = [];

    const address = (storedAnswers?.["address"] as string || "").trim();
    if (address) {
        parts.push(`Our address is ${address}`);
    }

    const hoursPart = formatHoursParagraph(storedAnswers?.["weeklyHours"], moduleFieldLookup);
    if (hoursPart) {
        parts.push(hoursPart);
    }

    const storedSpecialHours = storedAnswers?.["specialHours"];
    const closures = storedSpecialHours
        ? formatSpecialDatesForDisplay(storedSpecialHours as any)
        : formatSpecialDatesForDisplay(getModuleFieldValue(moduleFieldLookup, "DISABLED_DAYS", ""));
    if (closures) {
        parts.push(`Closures: ${closures.replace(/\n/g, "; ")}`);
    }

    const popularItems = (storedAnswers?.["popularItems"] as string || "").trim();
    if (popularItems) {
        parts.push(formatPopularItemsParagraph(popularItems));
    }

    const cleanUserText = (userText || "").trim();
    if (cleanUserText) {
        parts.push(cleanUserText);
    }

    const baseText = parts.join("\n\n");
    return `${baseText}\n\n${VOCAL_FORMATTING_PROMPT}`;
};
